/**
 * Live-canvas capture for the PDF hero image and any other export that
 * embeds "what the viewer looks like".
 *
 * The export image is always the PHOTO view. Analysis colouring exists to
 * be read next to its legend and its toggle; a PDF has neither, so a
 * captured frame must be taken with those modes off and the on-screen
 * state restored immediately afterwards. Keeping that rule here — rather
 * than in the export action, which has no scene — means every consumer of
 * the snapshot getter inherits it.
 */
import type * as THREE from "three";
import { applyVentilationDiagramMode } from "../../engines/materials";

export interface ViewerSnapshotTarget {
	gl: {
		render: (scene: THREE.Object3D, camera: THREE.Camera) => void;
		domElement: {
			toDataURL: (type?: string, quality?: number) => string;
			/** `HTMLCanvasElement.isConnected`; optional for minimal targets. */
			isConnected?: boolean;
		};
		/**
		 * Present on a real WebGLRenderer. Optional so tests (and any other
		 * minimal target) can stay a plain object literal.
		 */
		getContext?: () => { isContextLost?: () => boolean } | null;
	};
	scene: THREE.Object3D;
	camera: THREE.Camera;
}

export interface ViewerSnapshotOptions {
	/**
	 * Encoding for the captured frame. PNG is lossless but synchronous and
	 * slow: encoding plus base64 of a full-size canvas is ~150-400 ms on
	 * desktop and up to ~1.5 s on mobile, and the resulting data URL is
	 * megabytes of string that the caller then holds in React state.
	 *
	 * Defaults to PNG so the PDF hero image keeps the exact bytes it has
	 * always had. Screen-facing consumers (the share dialog) ask for JPEG,
	 * the same choice `lib/buildStepSnapshots.ts` already makes.
	 */
	format?: "png" | "jpeg";
	/** JPEG quality, 0-1. Ignored for PNG. */
	quality?: number;
}

/**
 * Encoding for the share dialog's preview: what the user sees on screen and
 * downloads, never a print master. JPEG at 0.85 is visually indistinguishable
 * here and roughly an order of magnitude smaller and faster than PNG. The
 * viewer always draws an opaque sky, so the missing alpha channel costs
 * nothing.
 */
export const SHARE_SNAPSHOT_OPTIONS: ViewerSnapshotOptions = {
	format: "jpeg",
	quality: 0.85,
};

/**
 * Render `target` and return an image data URL with the analysis layers
 * neutralised, or `null` when the drawing surface cannot produce a frame.
 * `ventilationDiagram` is the layer's LIVE state: when it is on, the vents
 * are swapped back to real hardware for the capture and re-coloured (and
 * repainted) before returning.
 *
 * The getter outlives the canvas: the viewer only mounts on the design
 * workspace, so a snapshot requested from another workspace would run
 * against a torn-down renderer. That produces a blank frame rather than an
 * error, so consumers get `null` instead — a share dialog with no image
 * beats a share dialog with an empty rectangle.
 *
 * Two probes are needed, not one. R3F defers `forceContextLoss()` by 500 ms
 * on unmount, so for half a second `isContextLost()` still reports false
 * while the canvas is already detached and the scene's materials are being
 * disposed. Detachment (`isConnected`) catches that window; the context
 * probe catches a genuine GPU context loss on a still-mounted canvas.
 *
 * Caveat: while the section view is active its per-view material clones
 * own the vent faces, so the swap no-ops — a cutaway hero shot is not a
 * photo either way.
 */
export function captureViewerSnapshot(
	target: ViewerSnapshotTarget,
	ventilationDiagram: boolean,
	options?: ViewerSnapshotOptions,
): string | null {
	const { gl, scene, camera } = target;
	const mimeType = options?.format === "jpeg" ? "image/jpeg" : "image/png";

	if (gl.domElement.isConnected === false) {
		return null;
	}
	if (gl.getContext?.()?.isContextLost?.() === true) {
		return null;
	}

	if (ventilationDiagram) {
		applyVentilationDiagramMode(scene, false);
	}
	gl.render(scene, camera);
	const dataUrl =
		mimeType === "image/jpeg"
			? gl.domElement.toDataURL(mimeType, options?.quality ?? 0.85)
			: gl.domElement.toDataURL(mimeType);

	if (ventilationDiagram) {
		applyVentilationDiagramMode(scene, true);
		// Repaint so the on-screen canvas doesn't hold the neutral frame —
		// the viewer renders on demand, so nothing else would.
		gl.render(scene, camera);
	}

	return dataUrl;
}
